import { ISimpleAccountResponse } from '@/domains/AccountDomain'
import { OKSign } from '@/domains/CommonDomain'
import { CreditAccountKindCode, TransactionType } from '@/domains/TransferDomain'

export const ScheduleListRequestRowCount = 20

export const INFINITE_DATE = '9999-12-31'

export enum SchedulePaymentDueType {
  DAY = 'D',
  WEEK = 'W',
  MONTH = 'M',
}

export enum SchedulePaymentRepeatType {
  INFINITE = 0,
  ONE_TIME = 1,
}

export enum SchedulePaymentHistoryCancelType {
  EXPIRED = '01',
  USER_CANCEL = '02',
}

export enum SchedulePaymentTransactionPurposeType {
  FUND_TRANSFER = '02',
  PURCHASING = '03',
  INVESTMENT = '01',
  OTHERS = '99',
}

export type SchedulePaymentHistoryParams = Pick<
  ISchedulePaymentFormState,
  | 'bnfcAcctNo'
  | 'bnfcBankCd'
  | 'bnfcNm'
  | 'bnfcBankNm'
  | 'trscType'
  | 'myAcctYn'
  | 'trxAmt'
  | 'bnfcAcctKindCd'
  | 'bnfcAcctItmCd'
>

export type ISchedulePaymentIdentity = Pick<IScheduledTransfer, 'refNo'>

export enum SchedulePaymentTransferType {
  OVERBOOKING = '01',
  ONLINE = '04',
  BI_FAST = '06',
}

export interface ICommonTransfer {
  refNo: string // 정기이체번호
  acctNo: string // 출금계좌번호
  trxAmt: string // 거래금액
  bnfcBankNm: string // 수취은행명
  bnfcAcctNo: string // 수취계좌번호
  bnfcNm: string // 수취인이름
  startDtm: number // 정기이체 시작일자
  endDtm: number // 정기이체 종료일자
  dueType: SchedulePaymentDueType
  trsfDay: number
  trxNote?: string
  acctTitle: string
}

export interface ICommonTransferDetail extends ICommonTransfer {
  bnfcImgUrl: string // 수취인프로필이미지
  biPurpCd?: SchedulePaymentTransactionPurposeType // only BI-Fast (trsfTyCd: 06)
  trsfTpCd?: SchedulePaymentTransferType
}

export interface IScheduledTransfer extends ICommonTransfer {
  cancelable: boolean // 취소가능여부
  expirationDtm: number // 정기이체 예정일자
  trsfTpCd: SchedulePaymentTransferType
  trsfTpNm: string
}

export interface IScheduledTransferDetail extends ICommonTransferDetail {
  cancelable: boolean // 취소가능여부
  expirationDtm: number // 정기이체 예정일자
}

export interface IScheduledTransferHistory extends ICommonTransfer {
  cancelType: SchedulePaymentHistoryCancelType
  cancelDtm: number
  trsfTpCd: SchedulePaymentTransferType
  trsfTpNm: string
}

export interface IScheduledTransferHistoryDetail extends ICommonTransferDetail {
  cancelType: SchedulePaymentHistoryCancelType
  cancelDtm: number
}

export interface ISchedulePaymentFormState {
  acctNo: string // 출금계좌번호
  trxAmt: string // 거래금액
  myAcctYn: OKSign // 내계좌여부
  bnfcBankCd: string // 수취은행코드
  bnfcBankNm: string // 수취은행명
  bnfcAcctNo: string // 수취계좌번호
  bnfcNm: string // 수취인 이름 (crAcctNm1 + crAcctNm2)
  startDt: string // 예약이체 시작일자 (반복유형코드 - D: YYYY/MM/DD, W: YYYY/MM/DD, M: YYYY/MM/01)
  endDt: string // 예약이체 종료일자 (반복유형코드 - D: YYYY/MM/DD (예약이체 시작일자와 동일), W: YYYY/MM/DD, M: YYYY/MM/31 (매월 말일), Never End 설정 시 9999/12/31로 설정해서 전달필요)
  dueType: undefined | SchedulePaymentDueType // 기간유형 (D : 1회, W : 매주, M : 매월)
  trsfDay: undefined | number // 지정일자 D (1회) : 0, W (매주) : 1~7, M (매월) : 1~31 (29 ~ 31일은 1로 처리됨)
  trxNote: string // 거래노트
  selectedAccount: ISimpleAccountResponse | null
  isShowNumberPad: boolean
  trscType: TransactionType | null
  dueTimes: SchedulePaymentRepeatType // 반복유형 (0:무한반복, 1:One Time)
  isDirty: boolean // 키패드 눌림
  biPurpCd?: SchedulePaymentTransactionPurposeType // only BI-Fast
  bnfcAcctKindCd?: CreditAccountKindCode // only BI-Fast
  bnfcAcctItmCd?: string // only BI-Fast
}

export type IRegisterScheduledTransferResponse = ISchedulePaymentIdentity

// export const EmptyScheduledTransfer: IScheduledTransfer = {
//   refNo: 'DAT0888219002327',
//   acctNo: '110921200025912',
//   trxAmt: '180000',
//   acctTitle: 'Main Account',
//   bnfcBankNm: 'Mandiri Bank',
//   bnfcAcctNo: '110921200025913',
//   expirationDtm: 1562045027482,
//   bnfcNm: 'DEDY KURNIA SANTOSA',
//   startDtm: 1563462419489,
//   endDtm: 1569464419489,
//   dueType: 'D' as SchedulePaymentDueType,
//   trsfDay: 1,
//   trxNote: 'string',
//   cancelable: true,
// }

export const EmptyScheduledTransfer: IScheduledTransfer = {
  refNo: '',
  acctNo: '',
  trxAmt: '',
  acctTitle: '',
  bnfcBankNm: '',
  bnfcAcctNo: '',
  expirationDtm: 0,
  bnfcNm: '',
  startDtm: 0,
  endDtm: 0,
  dueType: '' as SchedulePaymentDueType,
  trsfDay: 0,
  trxNote: '',
  cancelable: false,
  trsfTpCd: SchedulePaymentTransferType.OVERBOOKING,
  trsfTpNm: '',
}

// export const EmptyScheduledTransferDetail: IScheduledTransferDetail = {
//   refNo: 'DAT0888219002327',
//   acctNo: '110921200025912',
//   trxAmt: '180000',
//   acctTitle: 'Main Account',
//   bnfcBankNm: 'Mandiri Bank',
//   bnfcAcctNo: '110921200025913',
//   expirationDtm: 1562045027482,
//   bnfcNm: 'DEDY KURNIA SANTOSA',
//   startDtm: 1561939200,
//   endDtm: 1564617600,
//   dueType: 'D' as SchedulePaymentDueType,
//   trsfDay: 1,
//   trxNote: 'string',
//   cancelable: true,
//   bnfcImgUrl:
//     'https://lfstore-phinf.pstatic.net/MjAxOTA1MjRfMTc2/MDAxNTU4NjU5NTM0MTc3.ALgsazkwRbRlnvFPYfCn7oehv3UdSvjMX3zBVAbdr6Ig.IJdxXaHXSGaD_xXqYb6phU9WPq0asaW6LRg6czqR5bcg.JPEG/image.jpg?type=u380_380',
// }

export const EmptyScheduledTransferDetail: IScheduledTransferDetail = {
  refNo: '',
  acctNo: '',
  trxAmt: '',
  acctTitle: '',
  bnfcBankNm: '',
  bnfcAcctNo: '',
  expirationDtm: 0,
  bnfcNm: '',
  startDtm: 0,
  endDtm: 0,
  dueType: '' as SchedulePaymentDueType,
  trsfDay: 0,
  trxNote: '',
  cancelable: false,
  bnfcImgUrl: '',
}

// export const EmptyScheduledTransferHistory: IScheduledTransferHistory = {
//   refNo: 'DAT0888219002327',
//   acctNo: '110921200025912',
//   trxAmt: '180000',
//   acctTitle: 'Main Account',
//   bnfcBankNm: 'Mandiri Bank',
//   bnfcAcctNo: '110921200025913',
//   bnfcNm: 'DEDY KURNIA SANTOSA',
//   startDtm: 1563462419489,
//   endDtm: 1569464419489,
//   dueType: 'D' as SchedulePaymentDueType,
//   trsfDay: 1,
//   trxNote: 'string',
//   cancelType: SchedulePaymentHistoryCancelType.EXPIRED,
//   cancelDtm: 1562045027482,
// }

export const EmptyScheduledTransferHistory: IScheduledTransferHistory = {
  refNo: '',
  acctNo: '',
  trxAmt: '',
  acctTitle: '',
  bnfcBankNm: '',
  bnfcAcctNo: '',
  bnfcNm: '',
  startDtm: 0,
  endDtm: 0,
  dueType: '' as SchedulePaymentDueType,
  trsfDay: 0,
  trxNote: '',
  cancelType: SchedulePaymentHistoryCancelType.EXPIRED,
  cancelDtm: 0,
  trsfTpCd: '' as SchedulePaymentTransferType,
  trsfTpNm: '',
}

// export const EmptyScheduledTransferHistoryDetail: IScheduledTransferHistoryDetail = {
//   refNo: 'DAT0888219002327',
//   acctNo: '110921200025912',
//   trxAmt: '180000',
//   acctTitle: 'Main Account',
//   bnfcBankNm: 'Mandiri Bank',
//   bnfcAcctNo: '110921200025913',
//   bnfcNm: 'DEDY KURNIA SANTOSA',
//   startDtm: 1563462419489,
//   endDtm: 1569464419489,
//   dueType: 'D' as SchedulePaymentDueType,
//   trsfDay: 1,
//   trxNote: 'string',
//   bnfcImgUrl:
//     'https://lfstore-phinf.pstatic.net/MjAxOTA1MjRfMTc2/MDAxNTU4NjU5NTM0MTc3.ALgsazkwRbRlnvFPYfCn7oehv3UdSvjMX3zBVAbdr6Ig.IJdxXaHXSGaD_xXqYb6phU9WPq0asaW6LRg6czqR5bcg.JPEG/image.jpg?type=u380_380',
//   cancelType: SchedulePaymentHistoryCancelType.EXPIRED,
//   cancelDtm: 1562045027482,
// }

export const EmptyScheduledTransferHistoryDetail: IScheduledTransferHistoryDetail = {
  refNo: '',
  acctNo: '',
  trxAmt: '',
  acctTitle: '',
  bnfcBankNm: '',
  bnfcAcctNo: '',
  bnfcNm: '',
  startDtm: 0,
  endDtm: 0,
  dueType: '' as SchedulePaymentDueType,
  trsfDay: 0,
  trxNote: '',
  bnfcImgUrl: '',
  cancelType: SchedulePaymentHistoryCancelType.EXPIRED,
  cancelDtm: 0,
}

export const schedulePaymentTransferTypeLabel = {
  [SchedulePaymentTransferType.BI_FAST]: 'schedulepayment_detail_transfer_type_bi_fast',
  [SchedulePaymentTransferType.ONLINE]: 'schedulepayment_detail_transfer_type_prima',
  [SchedulePaymentTransferType.OVERBOOKING]: 'schedulepayment_detail_transfer_type_overbooking',
}
